import { BigNumber } from '@ethersproject/bignumber'
import { Pool, TickMath } from '@uniswap/v3-sdk'
import JSBI from 'jsbi'
import { useMemo } from 'react'
import { sqrtToPrice } from 'utils/math'
import { priceToSqrt } from 'utils/math'

// FeeTier: price modifier
const PRICE_MODIFIERS = {
  100: {
    lower: 0.9921,
    upper: 1.0079,
  },
  500: {
    lower: 0.9949,
    upper: 1.006,
  },
  3000: {
    lower: 0.7,
    upper: 1.4,
  },
  10000: {
    lower: 0.25,
    upper: 2,
  },
}

// eslint-disable-next-line import/no-unused-modules
export function useMidPosition(pool: Pool | null, lower: number | null, upper: number | null) {
  const priceModifier = useMemo(() => {
    if (pool) {
      if (lower && upper) {
        return { lower, upper }
      }
      return PRICE_MODIFIERS[pool?.fee]
    }

    return { lower: 1, upper: 1 }
  }, [pool, lower, upper])

  const tickLower = useMemo(() => {
    try {
      if (!pool) return null

      let tickLower =
        priceModifier.lower !== 1
          ? TickMath.getTickAtSqrtRatio(
              JSBI.BigInt(
                priceToSqrt(
                  sqrtToPrice(pool.sqrtRatioX96, pool.token0.decimals, pool.token1.decimals, 6) * priceModifier.lower,
                  pool.token0.decimals,
                  pool.token1.decimals,
                  8
                )
              )
            )
          : pool.tickCurrent

      if (tickLower >= pool.tickCurrent) {
        tickLower = pool.tickCurrent - pool.tickSpacing
      }

      if (tickLower % pool.tickSpacing !== 0) {
        tickLower -= tickLower % pool.tickSpacing
      }

      if (tickLower >= pool.tickCurrent - pool.tickSpacing) {
        tickLower -= pool.tickSpacing
      }

      return tickLower
    } catch (error) {
      console.error(error)
      return null
    }
  }, [pool, priceModifier])

  const tickUpper = useMemo(() => {
    try {
      if (!pool) return null

      let tickUpper =
        priceModifier.upper !== 1
          ? TickMath.getTickAtSqrtRatio(
              JSBI.BigInt(
                priceToSqrt(
                  sqrtToPrice(pool.sqrtRatioX96, pool.token0.decimals, pool.token1.decimals, 6) * priceModifier.upper,
                  pool.token0.decimals,
                  pool.token1.decimals,
                  8
                )
              )
            )
          : pool.tickCurrent

      if (tickUpper <= pool.tickCurrent) {
        tickUpper = pool.tickCurrent + pool.tickSpacing
      }

      if (tickUpper % pool.tickSpacing !== 0) {
        tickUpper += pool.tickSpacing - (tickUpper % pool.tickSpacing)
      }

      if (tickUpper <= pool.tickCurrent + pool.tickSpacing) {
        tickUpper += pool.tickSpacing
      }

      return tickUpper
    } catch (error) {
      console.error(error)
      return null
    }
  }, [pool, priceModifier])

  const liquidity = pool?.liquidity
    ? BigNumber.from(JSBI.divide(pool?.liquidity, JSBI.BigInt(10)).toString())
    : BigNumber.from('0')

  return liquidity && tickLower && tickUpper ? { liquidity, tickLower, tickUpper } : null
}
